/**
 * Attendance API stores and returns instants in UTC (`OccurredAtUtc`, `DayStartUtc`, `fromUtc` / `toUtc`).
 * Display converts those instants on the **frontend** using the browser’s local IANA timezone
 * (`Intl.DateTimeFormat().resolvedOptions().timeZone`). That matches the user’s **current device /
 * OS location** (e.g. Pakistan vs UK after travel). We do **not** rely on office timezone from the
 * backend — it is not provided by the API; the browser supplies the active zone.
 */

/**
 * IANA timezone used for all attendance date/time display and for “local calendar day” boundaries
 * (reports, daily log). Always the **browser’s current** zone so travel and OS timezone changes apply.
 * @returns {string}
 */
export const getAttendanceDisplayTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Parse API datetime as an absolute instant in UTC.
 * Strings without an offset are treated as UTC (append `Z`), matching server contract.
 * @param {string|Date|number|null|undefined} raw
 * @returns {Date|null}
 */
export const parseUtcFromApi = (raw) => {
  if (raw == null || raw === "") return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  if (typeof raw === "number") {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof raw === "string") {
    const s = raw.trim();
    if (!s) return null;
    const hasTz =
      s.includes("Z") || /[+-]\d{2}:?\d{2}$/.test(s) || /[+-]\d{4}$/.test(s);
    const normalized = hasTz ? s : `${s}Z`;
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

/** @deprecated use parseUtcFromApi */
export const parseApiDate = parseUtcFromApi;

/**
 * Format an instant for display (time only) in the attendance display timezone.
 * @param {string|Date|number|null|undefined} rawUtc
 * @param {{ withSeconds?: boolean, timeZone?: string }} [opts]
 */
export const formatAttendanceTime = (rawUtc, opts = {}) => {
  const d = parseUtcFromApi(rawUtc);
  if (!d) return "—";
  const timeZone = opts.timeZone ?? getAttendanceDisplayTimeZone();
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    second: opts.withSeconds ? "2-digit" : undefined,
  }).format(d);
};

/**
 * Format an instant for display (weekday + calendar date) in the attendance display timezone.
 * @param {string|Date|number|null|undefined} rawUtc
 * @param {{ timeZone?: string }} [opts]
 */
export const formatAttendanceDateLong = (rawUtc, opts = {}) => {
  const d = parseUtcFromApi(rawUtc);
  if (!d) return "—";
  const timeZone = opts.timeZone ?? getAttendanceDisplayTimeZone();
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(d);
};

/** @deprecated use formatAttendanceTime */
export const formatLocalTime = formatAttendanceTime;

/** @deprecated use formatAttendanceDateLong */
export const formatLocalDateLong = formatAttendanceDateLong;

/**
 * Calendar Y-M-D string for an instant in a specific IANA zone (e.g. en-CA → YYYY-MM-DD).
 * @param {number|Date} msOrDate
 * @param {string} [timeZone]
 */
export const formatYmdInZone = (msOrDate, timeZone) => {
  const ms = msOrDate instanceof Date ? msOrDate.getTime() : Number(msOrDate);
  const tz = timeZone ?? getAttendanceDisplayTimeZone();
  const f = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const p = f.formatToParts(new Date(ms));
  const get = (t) => p.find((x) => x.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
};

/**
 * First day of the month containing `ms`, as YYYY-MM-DD in `timeZone`.
 * @param {number} ms
 * @param {string} [timeZone]
 */
export const startOfMonthYmdInZone = (ms, timeZone) => {
  const tz = timeZone ?? getAttendanceDisplayTimeZone();
  const f = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const p = f.formatToParts(new Date(ms));
  const get = (t) => p.find((x) => x.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-01`;
};

/**
 * UTC `Date` for 00:00 (start) of civil date (y, m, d) in `timeZone`.
 * @param {number} y
 * @param {number} m 1–12
 * @param {number} d 1–31
 * @param {string} [timeZone]
 * @returns {Date}
 */
export const zonedCalendarDateStartUtc = (y, m, d, timeZone) => {
  const tz = timeZone ?? getAttendanceDisplayTimeZone();
  const pad = (n) => String(n).padStart(2, "0");
  const target = `${y}-${pad(m)}-${pad(d)}`;
  const dateFmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const ymd = (ms) => {
    const p = dateFmt.formatToParts(new Date(ms));
    const get = (t) => p.find((x) => x.type === t)?.value ?? "";
    return `${get("year")}-${get("month")}-${get("day")}`;
  };
  const approx = Date.UTC(y, m - 1, d, 12, 0, 0);
  for (let step = approx - 72 * 3600000; step <= approx + 72 * 3600000; step += 60000) {
    if (ymd(step) === target && ymd(step - 60000) !== target) {
      return new Date(step);
    }
  }
  return new Date(approx);
};

/**
 * UTC `Date` for last millisecond of civil date (y, m, d) in `timeZone`.
 */
export const zonedCalendarDateEndUtc = (y, m, d, timeZone) => {
  const tz = timeZone ?? getAttendanceDisplayTimeZone();
  const t = Date.UTC(y, m - 1, d + 1);
  const nd = new Date(t);
  const y2 = nd.getUTCFullYear();
  const m2 = nd.getUTCMonth() + 1;
  const d2 = nd.getUTCDate();
  const startNext = zonedCalendarDateStartUtc(y2, m2, d2, tz);
  return new Date(startNext.getTime() - 1);
};

/**
 * @param {Date} d
 * @returns {string} ISO-8601 UTC for query params
 */
export const toUtcIso = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return d.toISOString();
};

/**
 * Value for `<input type="datetime-local" />` — wall date/time in `timeZone` for a UTC instant.
 * @param {string|Date|number|null|undefined} rawUtc
 * @param {string} [timeZone]
 * @returns {string} `YYYY-MM-DDTHH:mm` or ""
 */
export const formatUtcAsDatetimeLocalValue = (rawUtc, timeZone) => {
  const d = parseUtcFromApi(rawUtc);
  if (!d) return "";
  const tz = timeZone ?? getAttendanceDisplayTimeZone();
  const f = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const p = f.formatToParts(d);
  const get = (t) => p.find((x) => x.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
};

/**
 * Value for `<input type="time" />` — 24h `HH:mm` wall time in `timeZone` for a UTC instant.
 * @param {string|Date|number|null|undefined} rawUtc
 * @param {string} [timeZone]
 * @returns {string} `HH:mm` or ""
 */
export const formatUtcAsTimeInputValue = (rawUtc, timeZone) => {
  const d = parseUtcFromApi(rawUtc);
  if (!d) return "";
  const tz = timeZone ?? getAttendanceDisplayTimeZone();
  const f = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const p = f.formatToParts(d);
  const get = (ty) => p.find((x) => x.type === ty)?.value ?? "";
  const hh = get("hour").padStart(2, "0");
  const mm = get("minute").padStart(2, "0");
  return `${hh}:${mm}`;
};

/**
 * Interprets `YYYY-MM-DDTHH:mm` as local civil time in `timeZone` and returns the UTC instant.
 * @param {string|null|undefined} localValue
 * @param {string} [timeZone]
 * @returns {Date|null}
 */
export const parseDatetimeLocalValueToUtc = (localValue, timeZone) => {
  if (localValue == null || typeof localValue !== "string") return null;
  const m = localValue.trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const hh = Number(m[4]);
  const mm = Number(m[5]);
  if (![y, mo, d, hh, mm].every((n) => Number.isFinite(n))) return null;
  const tz = timeZone ?? getAttendanceDisplayTimeZone();
  const f = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const matches = (instantMs) => {
    const p = f.formatToParts(new Date(instantMs));
    const get = (t) => Number(p.find((x) => x.type === t)?.value ?? -1);
    return get("year") === y && get("month") === mo && get("day") === d && get("hour") === hh && get("minute") === mm;
  };
  const center = Date.UTC(y, mo - 1, d, hh, mm, 0);
  for (let delta = -48 * 60; delta <= 48 * 60; delta++) {
    const t = center + delta * 60000;
    if (matches(t)) return new Date(t);
  }
  return null;
};

/**
 * @param {string} ymd `YYYY-MM-DD`
 * @returns {[number, number, number]|null} [y, m, d]
 */
export const parseYmd = (ymd) => {
  if (!ymd || typeof ymd !== "string") return null;
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return [parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10)];
};

/**
 * Lexicographic compare for `YYYY-MM-DD` strings.
 * @returns {-1|0|1}
 */
export const compareYmd = (a, b) => {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

/**
 * Clamp `ymd` to the inclusive range `[minYmd, maxYmd]` (assumes valid ISO date strings).
 * @param {string} ymd
 * @param {string} minYmd
 * @param {string} maxYmd
 */
export const clampYmd = (ymd, minYmd, maxYmd) => {
  if (compareYmd(ymd, minYmd) < 0) return minYmd;
  if (compareYmd(ymd, maxYmd) > 0) return maxYmd;
  return ymd;
};

/**
 * Add signed calendar days to a civil date in `timeZone` (DST-safe).
 * @param {string} ymd `YYYY-MM-DD`
 * @param {number} deltaDays
 * @param {string} [timeZone]
 * @returns {string} `YYYY-MM-DD`
 */
export const addCalendarDaysToYmd = (ymd, deltaDays, timeZone) => {
  const tz = timeZone ?? getAttendanceDisplayTimeZone();
  const p = parseYmd(ymd);
  if (!p || !Number.isFinite(deltaDays)) return ymd;
  let ms = zonedCalendarDateStartUtc(p[0], p[1], p[2], tz).getTime();
  const dir = deltaDays >= 0 ? 1 : -1;
  for (let i = 0; i < Math.abs(deltaDays); i++) {
    const startYmd = formatYmdInZone(ms, tz);
    let probe = ms + dir * 60 * 60 * 1000;
    let guard = 0;
    while (formatYmdInZone(probe, tz) === startYmd && guard < 30) {
      probe += dir * 60 * 60 * 1000;
      guard++;
    }
    const np = parseYmd(formatYmdInZone(probe, tz));
    if (!np) break;
    ms = zonedCalendarDateStartUtc(np[0], np[1], np[2], tz).getTime();
  }
  return formatYmdInZone(ms, tz);
};

/**
 * Range for reports/exports: inclusive civil dates in display TZ → UTC instants for API.
 * @param {string} fromYmd
 * @param {string} toYmd
 * @param {string} [timeZone]
 * @returns {{ fromUtc: Date, toUtc: Date }}
 */
export const utcRangeForZonedYmdInclusive = (fromYmd, toYmd, timeZone) => {
  const tz = timeZone ?? getAttendanceDisplayTimeZone();
  let a = fromYmd;
  let b = toYmd;
  if (typeof a === "string" && typeof b === "string" && a > b) {
    const t = a;
    a = b;
    b = t;
  }
  const pa = parseYmd(a);
  const pb = parseYmd(b);
  if (!pa || !pb) {
    const now = Date.now();
    const ymd = formatYmdInZone(now, tz);
    const p = parseYmd(ymd);
    const start = zonedCalendarDateStartUtc(p[0], p[1], p[2], tz);
    const end = zonedCalendarDateEndUtc(p[0], p[1], p[2], tz);
    return { fromUtc: start, toUtc: end };
  }
  const [y1, m1, d1] = pa;
  const [y2, m2, d2] = pb;
  return {
    fromUtc: zonedCalendarDateStartUtc(y1, m1, d1, tz),
    toUtc: zonedCalendarDateEndUtc(y2, m2, d2, tz),
  };
};

/** @param {number} hours */
export const formatHoursShort = (hours) => {
  const h = Number(hours);
  if (!Number.isFinite(h)) return "—";
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

/**
 * Parse `HH:mm` or `HH:mm:ss` (time input / API) to minutes from midnight.
 * @param {string|null|undefined} raw
 * @returns {number|null}
 */
export const parseTimeStringToMinutesFromMidnight = (raw) => {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min) || h > 23 || min > 59) return null;
  return h * 60 + min;
};

/**
 * Validates workday times and check-in / check-out grace for the configuration screen.
 * Skips when flexible working window is on (schedule fields are not enforced).
 *
 * Rules: start ≠ end; start &lt; end (same calendar day); (end − start) &gt; (check-in grace + check-out grace).
 *
 * @param {{ workStart: string, workEnd: string, checkInGraceMinutes: number, checkOutGraceMinutes: number, allowFlexibleWorkingWindow?: boolean }} input
 * @returns {{ valid: boolean, reason: 'same'|'order'|'grace'|'parse'|null, span?: number, graceTotal?: number, graceIn?: number, graceOut?: number }}
 */
export const validateAttendanceWorkdayAndGrace = (input) => {
  const { workStart, workEnd, checkInGraceMinutes, checkOutGraceMinutes, allowFlexibleWorkingWindow } = input;
  if (allowFlexibleWorkingWindow) return { valid: true, reason: null };

  const start = parseTimeStringToMinutesFromMidnight(workStart);
  const end = parseTimeStringToMinutesFromMidnight(workEnd);
  const gIn = Math.max(0, Number(checkInGraceMinutes) || 0);
  const gOut = Math.max(0, Number(checkOutGraceMinutes) || 0);

  if (start == null || end == null) return { valid: false, reason: "parse" };

  if (start === end) return { valid: false, reason: "same" };
  if (start > end) return { valid: false, reason: "order" };

  const span = end - start;
  const graceTotal = gIn + gOut;
  if (span <= graceTotal) {
    return {
      valid: false,
      reason: "grace",
      span,
      graceTotal,
      graceIn: gIn,
      graceOut: gOut,
    };
  }

  return { valid: true, reason: null };
};
